import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { 
  Users, UserPlus, Phone, Edit2, X, Shield, Navigation, 
  ZoomIn, ZoomOut, RefreshCw, AlertTriangle, ChevronRight, Check, Megaphone, Save, Trash2, MapPin
} from 'lucide-react';
import { SEOUL_SPOTS, CATEGORY_LABELS } from '../../data/seoulSpots.js';
import { fetchAllCongestionData } from '../../services/seoulApi.js';
import { useKakaoMap } from '../../hooks/useKakaoMap.js';
import { locationService } from '../../services/locationService.js';
import { useUserStore } from '../../store/useUserStore.js';
import { AdBanner } from '../../components/common/AdBanner.jsx';
import { IS_FREE_LAUNCH_PHASE } from '../../config.js';
import CongestionBottomSheet from '../../components/map/CongestionBottomSheet.jsx';
import styles from './HomePage.module.css';

const CATEGORIES = ['all', 'police', 'hospital', 'embassy', 'culture', 'park', 'special', 'commerce', 'crowded', 'festival'];
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5분

// Haversine 알고리즘 (직선 거리 계산)
function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
  const R = 6371; // 지구의 반지름 (km)
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2); 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  return R * c; 
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const mapContainerRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCongestion, setSelectedCongestion] = useState('all'); // 추가: 혼잡도 필터 상태
  const [congestionMap, setCongestionMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [myLocation, setMyLocation] = useState(null);
  const [is1to1Visible, setIs1to1Visible] = useState(false); // 1:1 공유 단독 노출 모드
  const [privateSharings, setPrivateSharings] = useState([]); // 1:1 공유 목록
  const [privateMembersMap, setPrivateMembersMap] = useState(new Map()); // 1:1 멤버 실시간 위치

  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [manualAddress, setManualAddress] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // 터치 포인터가 없는 기기 = 데스크탑 (Mac Mini, Windows PC 등)
  const isDesktop = !window.matchMedia('(pointer: coarse)').matches;

  const [selectedRouteMember, setSelectedRouteMember] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [routeMode, setRouteMode] = useState('pedestrian');


  // --- Phase 6: Group States ---
  const [userId, setUserId] = useState(null);
  const [joinedGroups, setJoinedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('ansim_joined_groups');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [groupMembersMap, setGroupMembersMap] = useState({}); // { groupCode: [members] }
  const [groupNoticesMap, setGroupNoticesMap] = useState({}); // { groupCode: notice }
  const [groupCreatedAtMap, setGroupCreatedAtMap] = useState({}); // { groupCode: createdAt (ms) }
  const [currentTime, setCurrentTime] = useState(Date.now());

  // 1초 단위 타이머 구동
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const [isEditingNotice, setIsEditingNotice] = useState(false);
  const [editNoticeText, setEditNoticeText] = useState('');
  const [activeMemberGroupCode, setActiveMemberGroupCode] = useState(null);
  const isSharing = joinedGroups.length > 0;
  const [showGroupInput, setShowGroupInput] = useState(false);
  const [tempCode, setTempCode] = useState('');
  const [tempGroupName, setTempGroupName] = useState('');
  const [tempNickname, setTempNickname] = useState('');
  const [selectedGroupDetail, setSelectedGroupDetail] = useState(null);

  // 수익화 스토어
  const isPremium = useUserStore((state) => state.isPremium);
  const [isAdPlaying, setIsAdPlaying] = useState(false);

  // 공지 업데이트 핸들러
  const handleUpdateNotice = async (code) => {
    if (!code || loading) return;
    try {
      setLoading(true); // 공용 로딩 상태 활용 혹은 별도 로딩 필요
      await locationService.updateGroupNotice(code, editNoticeText);
      // 수동 setGroupNoticesMap 제거 (리스너가 처리)
      setIsEditingNotice(false);
      setEditNoticeText('');
    } catch (err) {
      console.error(err);
      alert(t('home.noticeUpdateFail'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotice = async (e, code) => {
    e.stopPropagation(); // 이벤트 버블링 차단
    if (!window.confirm(t('home.confirmNoticeDelete'))) return;
    try {
      await locationService.updateGroupNotice(code, '');
      setGroupNoticesMap(prev => ({ ...prev, [code]: '' }));
    } catch (err) {
      alert(t('home.noticeDeleteFail'));
    }
  };

  // 그룹 참여 로직 핸들러
  const handleJoinGroup = async () => {
    try {
      if (tempNickname.length < 1 || tempNickname.length > 10) throw new Error(t('home.invalidNameLength'));
      if (tempCode.length !== 6) throw new Error(t('home.invalidCode'));

      if (joinedGroups.some(g => g.code === tempCode)) {
        throw new Error(t('home.alreadyJoined'));
      }
      if (joinedGroups.length >= 3) {
        throw new Error(t('home.maxGroups'));
      }
      
      let joinedData = null;
      if (userId) {
        joinedData = await locationService.joinGroup(userId, tempCode, tempNickname);
      }
      const newGroups = [...joinedGroups, { code: tempCode, isHost: false, groupName: joinedData ? joinedData.groupName : '알 수 없음' }];
      setJoinedGroups(newGroups);
      localStorage.setItem('ansim_joined_groups', JSON.stringify(newGroups));
      setShowGroupInput(false);
      setTempCode('');
      setTempNickname('');
    } catch (error) {
      alert(error.message);
    }
  };

  // 새 그룹 생성 로직 핸들러
  const handleCreateGroup = async () => {
    try {
      if (tempGroupName.length < 1 || tempGroupName.length > 10) throw new Error(t('home.invalidGroupNameLength'));
      if (tempNickname.length < 1 || tempNickname.length > 10) throw new Error(t('home.invalidNameLength'));

      if (!IS_FREE_LAUNCH_PHASE && joinedGroups.length >= 3) {
        throw new Error('최대 3개의 그룹까지만 참여 가능합니다.');
      }

      // 두 번째 그룹부터는 일반 유저일 경우 광고 시청 필요 (론칭 1단계 모드에서는 광고 스킵)
      if (!IS_FREE_LAUNCH_PHASE && joinedGroups.length > 0 && !isPremium) {
        const confirmAd = window.confirm('추가 그룹을 생성하려면 보상형 광고를 시청해야 합니다. 시청하시겠습니까?');
        if (!confirmAd) return;
        
        // 가짜 광고 시청 대기 (3초)
        setIsAdPlaying(true);
        await new Promise(resolve => setTimeout(resolve, 3000));
        setIsAdPlaying(false);
        alert('광고 시청 완료!');
      }

      if (userId) {
        const result = await locationService.createGroup(userId, tempGroupName, tempNickname);
        const newGroups = [...joinedGroups, { code: result.code, isHost: true, groupName: result.groupName }];
        setJoinedGroups(newGroups);
        localStorage.setItem('ansim_joined_groups', JSON.stringify(newGroups));
        setShowGroupInput(false);
        setTempCode('');
        setTempGroupName('');
        setTempNickname('');
      }
    } catch (error) {
      alert(error.message || t('home.createError'));
    }
  };

  // 그룹 공유하기
  const handleShare = async (group) => {
    const text = t('home.shareText', { name: group.groupName, code: group.code });
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('home.shareTitle'),
          text: text,
        });
      } catch (err) {
        console.warn('공유하기가 취소되었거나 지원되지 않습니다.', err);
      }
    } else {
      navigator.clipboard.writeText(text);
      alert(t('home.shareSuccess'));
    }
  };

  // 그룹 퇴장 로직 핸들러
  const handleLeaveGroup = async (codeToLeave, manuallyLeft = true) => {
    const newGroups = joinedGroups.filter(g => g.code !== codeToLeave);
    setJoinedGroups(newGroups);
    localStorage.setItem('ansim_joined_groups', JSON.stringify(newGroups));
    
    setGroupMembersMap(prev => {
      const next = { ...prev };
      delete next[codeToLeave];
      return next;
    });

    if (activeMemberGroupCode === codeToLeave) {
      setActiveMemberGroupCode(null);
    }

    if (selectedGroupDetail && selectedGroupDetail.code === codeToLeave) {
      setSelectedGroupDetail(null);
    }

    if (newGroups.length === 0) {
      setShowGroupInput(false);
    }

    if (userId && manuallyLeft) {
      await locationService.leaveOrCloseGroup(userId, codeToLeave);
    }
  };

  // 남은 시간 포맷터 및 자동 해제 (24시간 = 86400000ms)
  const getRemainingTimeInfo = (createdAtMs) => {
    if (!createdAtMs) return null;
    const diff = 24 * 60 * 60 * 1000 - (currentTime - createdAtMs);
    if (diff <= 0) return { expired: true, text: '00:00:00', isWarning: true };
    
    const h = Math.floor(diff / (1000 * 60 * 60)).toString().padStart(2, '0');
    const m = Math.floor((diff / (1000 * 60)) % 60).toString().padStart(2, '0');
    const s = Math.floor((diff / 1000) % 60).toString().padStart(2, '0');
    return {
      expired: false,
      text: `${h}:${m}:${s}`,
      isWarning: diff < 60 * 60 * 1000 // 1시간 미만 남았을 때 빨간색 경고
    };
  };

  // 타이머 자동 해제 감지
  useEffect(() => {
    joinedGroups.forEach(g => {
      const cTime = groupCreatedAtMap[g.code];
      if (cTime && (currentTime - cTime) >= 24 * 60 * 60 * 1000) {
        alert(t('home.groupExpiredAlert', { name: g.groupName || '' }));
        handleLeaveGroup(g.code, true); // 수동 종료 처리하여 DB의 방 상태도 비활성화
      }
    });
  }, [currentTime, joinedGroups, groupCreatedAtMap]);

  // 지도 설정값 메모이제이션 (리렌더링 방지)
  const mapOptions = useMemo(() => ({
    center: { lat: 37.5665, lng: 126.9780 },
    level: 7
  }), []);

  const { 
    mapRef,
    initMap, renderMarkers, clearSpotOverlays, 
    renderMemberMarkers, clearMemberOverlays,
    panTo, setCenter, setLevel, getLevel, fitBoundsToMembers,
    drawRoute, clearRoute
  } = useKakaoMap(mapContainerRef, mapOptions);

  const location = useLocation();

  // 다른 탭에서 돌아왔을 때 지도가 깨지는 현상 수정
  useEffect(() => {
    if (location.pathname === '/' && mapRef.current) {
      setTimeout(() => {
        mapRef.current.relayout();
      }, 50);
    }
  }, [location.pathname, mapRef]);

  const fetchRoute = useCallback(async (member, mode) => {
    if (!myLocation) {
      alert(t('home.needMyLocation', { defaultValue: '내 위치를 먼저 확인해 주세요.' }));
      return;
    }
    
    setSelectedRouteMember(member);
    setRouteMode(mode);
    setLoading(true);

    try {
      let pathCoords = [];
      let totalDistance = 0;
      let totalTime = 0;

      if (mode === 'pedestrian') {
        const TMAP_APP_KEY = 'k1fzZlhPat453LbkbxbTT3V0HY7F6LPk79RxQiar';
        const response = await fetch(`https://apis.openapi.sk.com/tmap/routes/pedestrian?version=1&format=json`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'appKey': TMAP_APP_KEY
          },
          body: JSON.stringify({
            startX: myLocation.lng,
            startY: myLocation.lat,
            endX: member.lng,
            endY: member.lat,
            reqCoordType: 'WGS84GEO',
            resCoordType: 'WGS84GEO',
            startName: '출발지',
            endName: '도착지'
          })
        });
        
        if (!response.ok) throw new Error('경로를 찾을 수 없습니다.');
        const data = await response.json();
        
        if (data.features && data.features.length > 0) {
          totalDistance = data.features[0].properties.totalDistance;
          totalTime = data.features[0].properties.totalTime;
          data.features.forEach(feature => {
            if (feature.geometry.type === 'LineString') {
              feature.geometry.coordinates.forEach(coord => {
                pathCoords.push({ lng: coord[0], lat: coord[1] });
              });
            }
          });
        }
      } else {
        const REST_API_KEY = '6129d052a2b6afd7fdbd0dc9334073a0';
        const origin = `${myLocation.lng},${myLocation.lat}`;
        const destination = `${member.lng},${member.lat}`;
        
        const response = await fetch(`https://apis-navi.kakaomobility.com/v1/directions?origin=${origin}&destination=${destination}`, {
          headers: { 'Authorization': `KakaoAK ${REST_API_KEY}` }
        });
        
        if (!response.ok) throw new Error('경로를 찾을 수 없습니다.');
        const data = await response.json();
        
        if (data.routes && data.routes.length > 0) {
          const route = data.routes[0];
          totalDistance = route.summary.distance;
          totalTime = route.summary.duration;
          route.sections.forEach(section => {
            section.roads.forEach(road => {
              const vertexes = road.vertexes;
              for (let i = 0; i < vertexes.length; i += 2) {
                pathCoords.push({ lng: vertexes[i], lat: vertexes[i+1] });
              }
            });
          });
        }
      }
      
      if (pathCoords.length > 0) {
        drawRoute(pathCoords);
        setRouteInfo({ distance: totalDistance, duration: totalTime });
        fitBoundsToMembers([{ lat: myLocation.lat, lng: myLocation.lng }, member]);
      } else {
        alert(t('home.routeNotFound', { defaultValue: '경로를 찾을 수 없습니다.' }));
        clearRoute();
        setSelectedRouteMember(null);
      }
    } catch (e) {
      console.error(e);
      alert(t('home.routeError', { defaultValue: '경로 조회 중 오류가 발생했습니다.' }));
      clearRoute();
      setSelectedRouteMember(null);
    } finally {
      setLoading(false);
    }
  }, [myLocation, drawRoute, clearRoute, fitBoundsToMembers, t]);

  const handleMemberClick = useCallback((member) => {
    fetchRoute(member, 'pedestrian');
  }, [fetchRoute]);

  const handleManualLocationSearch = useCallback(() => {
    if (!manualAddress.trim()) return;
    if (!window.kakao || !window.kakao.maps || !window.kakao.maps.services) {
      alert('지도 서비스가 아직 로드되지 않았습니다.');
      return;
    }
    const ps = new window.kakao.maps.services.Places();
    ps.keywordSearch(manualAddress, (data, status) => {
      if (status === window.kakao.maps.services.Status.OK && data.length > 0) {
        setSearchResults(data);
      } else {
        alert(t('home.searchFail', { defaultValue: '검색 결과를 찾을 수 없습니다.' }));
        setSearchResults([]);
      }
    });
  }, [manualAddress, t]);

  const handleSelectSearchResult = useCallback((place) => {
    const addrText = place.place_name || place.road_address_name || place.address_name || manualAddress;
    
    // 최종 확인 추가
    const isConfirmed = window.confirm(`'${addrText}'(으)로 내 위치를 설정하시겠습니까?`);
    if (!isConfirmed) return;

    const lat = parseFloat(place.y);
    const lng = parseFloat(place.x);
    setMyLocation({ lat, lng });
    localStorage.setItem('ansim_manual_coords', JSON.stringify({ lat, lng }));
    localStorage.setItem('ansim_manual_address', addrText);
    
    setLocationPermissionDenied(false);
    setSearchResults([]);
    setManualAddress('');
  }, [manualAddress]);

  // 1. Firebase 인증 초기화
  useEffect(() => {
    locationService.initAuth().then(async user => {
      setUserId(user.uid);
      console.log('User authenticated:', user.uid);
      
      // 구 버전의 로컬 스토리지 키 정리 및 초기화
      const legacyGroupCode = localStorage.getItem('ansim_group_code');
      if (legacyGroupCode) {
        localStorage.removeItem('ansim_group_code');
        localStorage.removeItem('ansim_is_sharing');
      }
    }).catch(console.error);

    // 알림 권한 요청 (브라우저 지원 여부 확인)
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, []);

  // 1.5 1:1 공유 목록 및 파트너 위치 구독
  useEffect(() => {
    if (!userId) return;
    const unsubSharings = locationService.subscribePrivateSharing(userId, (data) => {
      setPrivateSharings(data);
      
      // 1:1 파트너들의 ID 추출하여 위치 구독 시작
      const partnerIds = data
        .filter(s => s.status !== 'deleted')
        .map(s => (s.fromId === userId ? s.toPhone : s.fromId));
      
      const unsubLocations = locationService.subscribeMultipleUsers(partnerIds, (users) => {
        setPrivateMembersMap(users);
      });
      
      return () => unsubLocations();
    });
    return () => unsubSharings();
  }, [userId]);

  // 2. 다중 그룹 멤버 구독 및 상태(폭파) 감지
  useEffect(() => {
    if (joinedGroups.length === 0) {
      setGroupMembersMap({});
      return;
    }

    const unsubscribes = [];
    joinedGroups.forEach(g => {
      // 1) 그룹 상태 및 공지 감지 리스너
      unsubscribes.push(locationService.subscribeGroupStatus(g.code, ({ isActive, notice, createdAt }) => {
        if (!isActive && !g.isHost) {
          alert(t('home.groupClosedAlert', { name: g.groupName || '' }));
          handleLeaveGroup(g.code, false);
          return;
        }

        if (createdAt) {
          setGroupCreatedAtMap(prev => ({ ...prev, [g.code]: createdAt }));
        }

        // 공지 알림 로직
        setGroupNoticesMap(prev => {
          const oldNotice = prev[g.code];
          // 공지가 새로 생겼거나 내용이 변경된 경우
          if (oldNotice !== undefined && notice && oldNotice !== notice) {
            // 웹 알림은 조심스럽게 시도
            try {
              if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
                new Notification(t('home.noticeNotification', { name: g.groupName || '' }), {
                  body: notice,
                  icon: '/pwa-192x192.png'
                });
              }
            } catch (e) {
              console.warn('Notification failed', e);
            }
          }
          return { ...prev, [g.code]: notice || '' };
        });
      }));
      // 2) 그룹 멤버 리스너
      unsubscribes.push(locationService.subscribeGroupMembers(g.code, (members) => {
        setGroupMembersMap(prev => ({ ...prev, [g.code]: members }));
      }));
    });

    return () => unsubscribes.forEach(u => u());
  }, [joinedGroups]);

  // 3. 내 위치 실시간 업데이트 (실제 GPS 연동)
  useEffect(() => {
    if (!navigator.geolocation) return;

    const updateLoc = (position) => {
      const loc = { 
        lat: position.coords.latitude, 
        lng: position.coords.longitude 
      };
      setMyLocation(loc); // 내 위치 상태 업데이트
    };

    const handleLocError = (error) => {
      console.warn('위치 정보 획득 에러:', error.message, error.code);
      // code 1: PERMISSION_DENIED, code 2: POSITION_UNAVAILABLE (GPS 없는 데스크탑), code 3: TIMEOUT
      // 모든 에러 케이스에서 저장된 수동 좌표를 우선 사용하고, 없으면 수동 설정 UI 노출
      const savedCoords = localStorage.getItem('ansim_manual_coords');
      if (savedCoords) {
        try {
          setMyLocation(JSON.parse(savedCoords));
        } catch (e) {
          setLocationPermissionDenied(true);
        }
      } else {
        setLocationPermissionDenied(true);
      }
    };

    // 1) 즉시 초기 위치 한번 가져오기
    navigator.geolocation.getCurrentPosition(updateLoc, handleLocError, { timeout: 10000 });

    // 2) 위치 추적 시작
    const watchId = navigator.geolocation.watchPosition(
      updateLoc,
      handleLocError,
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 5000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [userId]);

  // 3.5 내 위치 동기화 (수동/자동 상관없이 위치가 있고 공유 중이면 DB 업데이트)
  useEffect(() => {
    if (isSharing && userId && myLocation) {
      locationService.updateMyLocation(userId, myLocation);
    }
  }, [isSharing, userId, myLocation]);

  const lastFocusedGroupRef = useRef(null);

  // 4. 멤버 마커 렌더링
  useEffect(() => {
    if (!mapReady) return;

    // 1. 모든 참여 그룹의 멤버를 하나의 Map으로 병합 (중복 제거 및 빠른 조회용)
    const memberMap = new Map();
    Object.values(groupMembersMap).forEach(members => {
      members.forEach(m => memberMap.set(m.uid, m));
    });

    let allMembers = [];
    if (activeMemberGroupCode) {
      allMembers = groupMembersMap[activeMemberGroupCode] || [];
    } else {
      allMembers = Array.from(memberMap.values());
    }

    // 내 위치 찾기
    const me = allMembers.find(m => m.uid === userId);
    
    const enrichedMembers = allMembers.map(member => {
      if (member.uid === userId || !me || !me.lat || !me.lng || !member.lat || !member.lng) {
        return member;
      }
      
      const distKm = getDistanceFromLatLonInKm(me.lat, me.lng, member.lat, member.lng);
      const walkTimeMins = Math.round(distKm * 15);
      
      let distText = distKm < 1 ? t('home.distanceM', { n: Math.round(distKm * 1000) }) : t('home.distanceKm', { n: distKm.toFixed(1) });
      let timeText = walkTimeMins < 60 ? t('home.timeMin', { n: walkTimeMins }) : t('home.timeHourMin', { h: Math.floor(walkTimeMins/60), m: walkTimeMins%60 });
      
      return {
        ...member,
        distanceText: distText,
        walkTimeText: timeText
      };
    });

    // 1:1 공유 멤버 데이터 가공
    const visiblePrivateMembers = privateSharings
      .filter(s => s.status !== 'deleted')
      .map(s => {
        const isFromMe = s.fromId === userId;
        const memberId = isFromMe ? s.toPhone : s.fromId;
        
        let locData = null;
        let sharedAtText = null;

        if (s.status === 'active') {
          locData = privateMembersMap.get(memberId);
        } else if (s.sharedLocation) {
          locData = s.sharedLocation;
          sharedAtText = s.sharedLocation.sharedAt ? new Date(s.sharedLocation.sharedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : null;
        }

        if (!locData) return null;

        return {
          ...locData,
          uid: memberId,
          nickname: s.customName || (isFromMe ? s.toPhone : s.fromNickname),
          isPrivateSharing: true,
          sharedAtText: sharedAtText
        };
      })
      .filter(m => m !== null);

    // [중요] 노출 모드 결정: 둘 중 하나라도 켜져 있으면 해당 마커들을 보여줌 (합집합)
    let allMembersToShow = [];
    if (activeMemberGroupCode) {
      allMembersToShow = [...allMembersToShow, ...enrichedMembers];
    }
    if (is1to1Visible) {
      allMembersToShow = [...allMembersToShow, ...visiblePrivateMembers];
    }

    // [추가] 내 위치 마커 상시 노출: 어떤 모드에서도 내 위치가 파악되면 마커 리스트에 추가
    if (myLocation && !allMembersToShow.find(m => m.uid === userId)) {
      allMembersToShow.push({
        uid: userId,
        lat: myLocation.lat,
        lng: myLocation.lng,
        nickname: t('home.me')
      });
    }

    if (allMembersToShow.length > 0) {
      renderMemberMarkers(allMembersToShow, userId, handleMemberClick);
    } else {
      clearMemberOverlays();
    }

    // 포커싱 로직
    if (is1to1Visible) {
      // 1:1 모드 진입 시 마커들에 포커스 (최초 1회 또는 멤버 변경 시)
      if (lastFocusedGroupRef.current !== '1to1' && visiblePrivateMembers.length > 0) {
        fitBoundsToMembers(visiblePrivateMembers);
        lastFocusedGroupRef.current = '1to1';
      }
    } else if (activeMemberGroupCode) {
      if (lastFocusedGroupRef.current !== activeMemberGroupCode && enrichedMembers.length > 0) {
        fitBoundsToMembers(enrichedMembers);
        lastFocusedGroupRef.current = activeMemberGroupCode;
      }
    } else {
      lastFocusedGroupRef.current = null;
    }
  }, [mapReady, groupMembersMap, userId, renderMemberMarkers, clearMemberOverlays, activeMemberGroupCode, fitBoundsToMembers, privateSharings, privateMembersMap, is1to1Visible, myLocation, handleMemberClick]);

  // 카카오맵 동적 로드 및 초기화
  useEffect(() => {
    if (mapReady) return; 

    const scriptId = 'kakao-map-sdk';
    let script = document.getElementById(scriptId);

    const initMapWithKakao = () => {
      if (window.kakao && window.kakao.maps) {
        window.kakao.maps.load(() => {
          if (!mapReady) {
            initMap();
            setMapReady(true);
          }
        });
      } else {
        // 모바일 등 네트워크가 느린 환경에서는 스크립트 로딩이 지연될 수 있으므로 재시도 (Polling)
        setTimeout(initMapWithKakao, 200);
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${import.meta.env.VITE_KAKAO_MAP_API_KEY}&libraries=services,clusterer&autoload=false`;
      script.async = true;
      script.onload = initMapWithKakao;
      document.head.appendChild(script);
    } else {
      setTimeout(initMapWithKakao, 100);
    }
  }, []);

  // 필터된 거점 메모이제이션
  const filteredSpots = useMemo(() => {
    return SEOUL_SPOTS.filter(spot => {
      // 1. 카테고리 필터링
      let matchesCategory = false;
      if (selectedCategory === 'all') {
        matchesCategory = !['police', 'hospital', 'embassy'].includes(spot.category);
      } else {
        matchesCategory = spot.category === selectedCategory;
      }
      
      // 'festival' 특수 카테고리 처리
      if (selectedCategory === 'festival') {
        const data = congestionMap.get(spot.id);
        matchesCategory = data?.events && data.events.length > 0;
      }

      if (!matchesCategory) return false;

      // 2. 혼잡도 필터링
      if (selectedCongestion === 'all') return true;
      
      const data = congestionMap.get(spot.id);
      if (!data) return false;

      const level = data.congestionLevel;
      if (selectedCongestion === '여유') return level === 1;
      if (selectedCongestion === '보통') return level === 2;
      if (selectedCongestion === '약간 붐빔') return level === 3;
      if (selectedCongestion === '붐빔') return level === 4;
      if (selectedCongestion === '매우 붐빔') return level === 5;

      return true;
    });
  }, [selectedCategory, selectedCongestion, congestionMap]);

  // API 데이터 로딩
  const loadCongestionData = useCallback(async (isAutoRefresh = false) => {
    if (!mapReady || (loading && !isAutoRefresh)) return;
    if (congestionMap.size > 0 && !isAutoRefresh) return;

    setLoading(true);
    setError(null);

    try {
      const newMap = await fetchAllCongestionData();
      setCongestionMap(newMap);
    } catch (e) {
      setError(e.message);
      setCongestionMap(new Map());
    } finally {
      setLoading(false);
    }
  }, [mapReady, loading, congestionMap.size]);

  const getCategoryCount = useCallback((category) => {
    if (category === 'all') {
      return SEOUL_SPOTS.filter(s => !['police', 'hospital', 'embassy'].includes(s.category)).length;
    }
    if (category === 'festival') {
      return Array.from(congestionMap.values()).filter(data => data.events && data.events.length > 0).length;
    }
    return SEOUL_SPOTS.filter(s => s.category === category).length;
  }, [congestionMap]);

  useEffect(() => {
    if (!mapReady) return;
    
    // 공유 모드(그룹 또는 1:1)가 활성화되어 있으면 서울시 장소 마커를 숨김
    if (activeMemberGroupCode || is1to1Visible) {
      clearSpotOverlays();
    } else if (congestionMap.size > 0) {
      const currentLang = i18n.language.startsWith('ko') ? 'ko' : 'en';
      renderMarkers(filteredSpots, congestionMap, handleMarkerClick, currentLang);
    }
  }, [mapReady, filteredSpots, congestionMap, activeMemberGroupCode, is1to1Visible, i18n.language]);

  useEffect(() => {
    loadCongestionData();
    const interval = setInterval(() => loadCongestionData(true), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadCongestionData]);

  function handleMarkerClick(spot, data) {
    setSelectedSpot(spot);
    setSelectedData(data);
    setSheetExpanded(true);
    panTo(spot.lat, spot.lng);
    
    clearRoute();
    setSelectedRouteMember(null);
    setRouteInfo(null);
  }

  function handleMyLocation() {
    // 1. 이미 추적된 위치 정보가 있으면 즉시 이동 (반응성 극대화)
    if (myLocation) {
      setCenter(myLocation.lat, myLocation.lng);
      setLevel(5);
      return;
    }

    // 2. 정보가 없으면 GPS 요청
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const loc = { lat: coords.latitude, lng: coords.longitude };
        setMyLocation(loc);
        setCenter(loc.lat, loc.lng);
        setLevel(5);
      },
      (error) => {
        // GPS 없는 데스크탑(code 2)이나 타임아웃(code 3)도 수동 설정 UI로 유도
        setLocationPermissionDenied(true);
      }
    );
  }

  const lang = i18n.language.startsWith('ko') ? 'ko' : 'en';

  return (
    <div className={styles.container}>
      <div ref={mapContainerRef} className={styles.mapContainer} id="kakao-map" />

      <div className="top-filter-bar">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            id={`filter-${cat}`}
            className={`filter-chip${selectedCategory === cat ? ' active' : ''}`}
            onClick={() => {
              setSelectedCategory(cat);
              setSelectedSpot(null);
              setSheetExpanded(false);
              clearRoute();
              setSelectedRouteMember(null);
              setRouteInfo(null);
            }}
            aria-label={`${cat === 'all' ? t('category.all') : (CATEGORY_LABELS[cat]?.[lang] || cat)} 필터 적용`}
          >
            {cat === 'all' ? t('category.all') : (CATEGORY_LABELS[cat]?.[lang] || cat)}
            <span className="chip-count">({getCategoryCount(cat)})</span>
          </button>
        ))}
      </div>

      <div className={styles.hotspotBanner}>
        <div className={styles.hotspotBannerTitle}>{t('home.hotspotWarningTitle')}</div>
        <div className={styles.hotspotBannerDesc}>{t('home.hotspotWarningDesc')}</div>
      </div>

      {loading && (
        <div className={styles.loadingBadge}>
          <div className="spinner" style={{ width: 16, height: 16 }} />
          <span>{t('map.loading')}</span>
        </div>
      )}

      {error && !loading && (
        <div className={styles.errorBadge}>
          <AlertTriangle size={14} />
          <span>{t('map.loadError')}</span>
          <button onClick={loadCongestionData} className={styles.retryBtn}>
            {t('map.retry')}
          </button>
        </div>
      )}

      <div className={styles.groupControls}>
        {joinedGroups.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
            {joinedGroups.map(g => (
              <div key={g.code} className={styles.groupBadgeRow}>
                <div className={styles.groupBadge} onClick={() => setSelectedGroupDetail(g)}>
                  <div className={styles.pulse} />
                  [{g.groupName || '그룹'}] ({(groupMembersMap[g.code] || []).length}명)
                  {groupNoticesMap[g.code] && (
                    <div className={styles.badgeNoticeContainer}>
                      <Megaphone size={14} fill="#ef4444" />
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => {
                    if (activeMemberGroupCode === g.code) {
                      setActiveMemberGroupCode(null);
                      clearRoute();
                      setSelectedRouteMember(null);
                      setRouteInfo(null);
                    } else {
                      setActiveMemberGroupCode(g.code);
                    }
                  }}
                  className={activeMemberGroupCode === g.code ? styles.eyeBtnActive : styles.eyeBtn}
                >
                  {activeMemberGroupCode === g.code ? t('home.hideMember') : t('home.viewMember')}
                </button>
              </div>
            ))}
            {joinedGroups.length < 3 && (
              <button 
                className={styles.addGroupBtn}
                onClick={() => setShowGroupInput(true)}
              >
                {t('home.addNewGroup')}
              </button>
            )}
          </div>
        ) : (
          <button 
            className="btn-icon" 
            onClick={() => setShowGroupInput(true)}
            title="그룹 위치 공유"
            aria-label="그룹 위치 공유"
          >
            <Users size={20} />
          </button>
        )}
      </div>

      {selectedRouteMember && routeInfo && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '400px',
          background: 'white',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 100,
          border: '2px solid #3b82f6'
        }}>
          <div className="flex justify-between items-center mb-2">
            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
              <Navigation size={16} className="inline mr-2 text-blue-500" />
              {selectedRouteMember.nickname} 님과의 경로
            </h4>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px' }}>
                <button 
                  onClick={() => fetchRoute(selectedRouteMember, 'pedestrian')}
                  style={{ 
                    border: 'none', background: routeMode === 'pedestrian' ? 'white' : 'transparent', 
                    borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: routeMode === 'pedestrian' ? 'bold' : 'normal',
                    color: routeMode === 'pedestrian' ? '#3b82f6' : '#64748b', cursor: 'pointer',
                    boxShadow: routeMode === 'pedestrian' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  🚶 도보
                </button>
                <button 
                  onClick={() => fetchRoute(selectedRouteMember, 'car')}
                  style={{ 
                    border: 'none', background: routeMode === 'car' ? 'white' : 'transparent', 
                    borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: routeMode === 'car' ? 'bold' : 'normal',
                    color: routeMode === 'car' ? '#3b82f6' : '#64748b', cursor: 'pointer',
                    boxShadow: routeMode === 'car' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  🚗 차량
                </button>
              </div>
              <button 
                onClick={() => {
                  clearRoute();
                  setSelectedRouteMember(null);
                  setRouteInfo(null);
                }}
                style={{ background: '#e2e8f0', border: 'none', borderRadius: '50%', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={16} color="#475569" />
              </button>
            </div>
          </div>
          <div style={{ fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: '600', color: '#3b82f6' }}>
              {routeInfo.distance >= 1000 ? (routeInfo.distance/1000).toFixed(1) + 'km' : routeInfo.distance + 'm'}
            </span>
            <span style={{ color: '#cbd5e1' }}>|</span>
            <span>{routeMode === 'pedestrian' ? '도보' : '차량'} 예상 {Math.ceil(routeInfo.duration / 60)}분</span>
          </div>
        </div>
      )}

      {/* 수동 위치 설정 글로벌 모달 */}
      {locationPermissionDenied && (
        <div className={styles.modalOverlay} style={{ zIndex: 1000 }}>
          <div className={styles.groupModal}>
            <button 
              className={styles.closeIconBtn} 
              onClick={() => setLocationPermissionDenied(false)}
              aria-label="닫기"
            >
              <X size={20} />
            </button>
            <h3>{t('home.manualLocationTitle', { defaultValue: '내 위치 수동 설정' })}</h3>
            <p className={styles.modalDesc} style={{ marginBottom: '10px' }}>
              {t('home.manualLocationDesc', { defaultValue: '위치 접근이 제한되어 있습니다. 원활한 서비스 이용을 위해 장소명(예: 강남역)이나 주소를 검색해 주세요.' })}
            </p>
            
            <div className={styles.inputGroup} style={{ marginBottom: '20px' }}>
              <div className={styles.inputRow}>
                <input 
                  type="text" 
                  placeholder={t('home.manualLocationPlaceholder', { defaultValue: '장소명 또는 주소 검색' })}
                  value={manualAddress}
                  onChange={(e) => setManualAddress(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleManualLocationSearch();
                  }}
                />
                <button 
                  className={`btn-primary ${styles.joinBtn}`}
                  onClick={handleManualLocationSearch}
                >
                  {t('home.searchBtn', { defaultValue: '검색' })}
                </button>
              </div>
            </div>
            
            {searchResults.length > 0 && (
              <ul style={{ 
                listStyle: 'none', padding: 0, margin: '12px 0 0', maxHeight: '200px', overflowY: 'auto', 
                border: '1px solid var(--color-border)', borderRadius: '8px', background: '#f8fafc' 
              }}>
                {searchResults.map((place, idx) => (
                  <li key={idx} 
                    style={{ 
                      padding: '12px', borderBottom: idx === searchResults.length - 1 ? 'none' : '1px solid #e2e8f0', 
                      cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px' 
                    }}
                    onClick={() => handleSelectSearchResult(place)}
                  >
                    <span style={{ fontWeight: 'bold', color: '#1e293b', fontSize: '14px' }}>
                      {place.place_name || place.address_name}
                    </span>
                    {(place.road_address_name || place.address_name) && (
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        {place.road_address_name || place.address_name}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {showGroupInput && (
        <div className={styles.modalOverlay}>
          <div className={styles.groupModal}>
            <button 
              className={styles.closeIconBtn} 
              onClick={() => {
                setShowGroupInput(false);
                setTempCode('');
                setTempNickname('');
                setTempGroupName('');
              }}
              aria-label="닫기"
            >
              <X size={20} />
            </button>
            <h3>{t('home.groupModalTitle')}</h3>
            <p className={styles.modalDesc} style={{ marginBottom: '10px' }}>{t('home.groupModalDesc')}</p>
            
            <div className={styles.inputGroup} style={{ marginBottom: '20px' }}>
              <input 
                type="text" 
                placeholder={t('home.namePlaceholder')}
                value={tempNickname}
                onChange={(e) => setTempNickname(e.target.value.slice(0, 10))}
                style={{ border: '1.5px solid var(--color-primary)', background: '#f0f9ff' }}
              />
            </div>
            
            <div className={styles.divider} style={{ margin: '15px 0' }}>{t('home.joinDivider')}</div>
            
            <div className={styles.inputGroup}>
              <div className={styles.inputRow}>
                <input 
                  type="text" 
                  placeholder={t('home.codePlaceholder')}
                  value={tempCode}
                  onChange={(e) => setTempCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                />
                <button 
                  className={`btn-primary ${styles.joinBtn}`}
                  onClick={handleJoinGroup}
                >
                  {t('home.joinBtn')}
                </button>
              </div>
            </div>
            
            <div className={styles.divider} style={{ margin: '15px 0' }}>{t('home.createDivider')}</div>
            
            <div className={styles.inputGroup}>
              <div className={styles.inputRow}>
                <input 
                  type="text" 
                  placeholder={t('home.groupNamePlaceholder')}
                  value={tempGroupName}
                  onChange={(e) => setTempGroupName(e.target.value.slice(0, 10))}
                />
                <button 
                  className={`btn-primary ${styles.joinBtn}`}
                  disabled={isAdPlaying}
                  onClick={handleCreateGroup}
                >
                  {isAdPlaying ? '광고 시청 중...' : (!IS_FREE_LAUNCH_PHASE && joinedGroups.length > 0 && !isPremium ? `${t('home.createBtn')}(AD)` : t('home.createBtn'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedGroupDetail && (
        <div className={styles.modalOverlay}>
          <div className={styles.groupModal}>
            <button 
              className={styles.closeIconBtn} 
              onClick={() => setSelectedGroupDetail(null)}
              aria-label="닫기"
            >
              <X size={20} />
            </button>
            <h3>{t('home.groupDetailTitle', { name: selectedGroupDetail.groupName || '' })}</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <p className={styles.modalDesc} style={{ margin: 0 }}>{t('home.code')} <strong>{selectedGroupDetail.code}</strong></p>
              {groupCreatedAtMap[selectedGroupDetail.code] && (() => {
                const rInfo = getRemainingTimeInfo(groupCreatedAtMap[selectedGroupDetail.code]);
                if (!rInfo) return null;
                return (
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: rInfo.isWarning ? '#ef4444' : '#f59e0b' }}>
                    {t('home.remainingTime', { time: rInfo.text })}
                  </div>
                );
              })()}
            </div>
            
            {/* 공지사항 영역 */}
            <div className={styles.noticeSection}>
              <div className={styles.noticeHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Megaphone size={16} color="#f59e0b" />
                  <span className={styles.noticeTitle}>그룹 공지</span>
                </div>
                {selectedGroupDetail?.isHost && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button 
                      className={styles.noticeEditBtn}
                      disabled={loading}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isEditingNotice) {
                          handleUpdateNotice(selectedGroupDetail.code);
                        } else {
                          setEditNoticeText(groupNoticesMap[selectedGroupDetail.code] || '');
                          setIsEditingNotice(true);
                        }
                      }}
                    >
                      {isEditingNotice ? <Save size={14} /> : <Edit2 size={14} />}
                    </button>
                    {!isEditingNotice && groupNoticesMap[selectedGroupDetail.code] && (
                      <button 
                        className={styles.noticeDeleteBtn}
                        disabled={loading}
                        onClick={(e) => handleDeleteNotice(e, selectedGroupDetail.code)}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {isEditingNotice ? (
                <div className={styles.noticeInputWrapper}>
                  <textarea 
                    className={styles.noticeInput}
                    value={editNoticeText}
                    onChange={(e) => setEditNoticeText(e.target.value)}
                    placeholder={t('home.noticePlaceholder')}
                    rows={2}
                    autoFocus
                  />
                </div>
              ) : (
                <div className={styles.noticeContent}>
                  {groupNoticesMap[selectedGroupDetail?.code] || t('home.noticePlaceholder')}
                </div>
              )}
            </div>
            
            <div className={styles.divider}>{t('home.viewMember')}</div>
            <p className={styles.modalDesc} style={{ marginBottom: '8px' }}>
              {t('settings.persons', { n: (groupMembersMap[selectedGroupDetail.code] || []).length })}
            </p>
            
            <ul className={styles.memberList}>
              {(() => {
                const members = groupMembersMap[selectedGroupDetail.code] || [];
                const me = members.find(m => m.uid === userId);
                
                return members.map(m => {
                  let distText = '위치 파악 중';
                  if (m.uid === userId) {
                    distText = '현재 내 위치';
                  } else if (me && me.lat && me.lng && m.lat && m.lng) {
                    const distKm = getDistanceFromLatLonInKm(me.lat, me.lng, m.lat, m.lng);
                    distText = distKm < 1 ? `${Math.round(distKm * 1000)}m 거리` : `${distKm.toFixed(1)}km 거리`;
                  }

                  return (
                    <li key={m.uid} className={styles.memberItem}>
                      <span>
                        <strong>{m.nickname || '이름 없음'}</strong> 
                        {m.uid === userId && <span style={{color: '#3b82f6', marginLeft: '4px'}}>(나)</span>}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                        {distText}
                      </span>
                    </li>
                  );
                });
              })()}
            </ul>
            
            <button className={styles.shareBtn} onClick={() => handleShare(selectedGroupDetail)}>
              {t('home.shareInvite')}
            </button>
            
            <button 
              className={styles.leaveBtn}
              onClick={() => handleLeaveGroup(selectedGroupDetail.code)}
              style={{ marginTop: '10px' }}
            >
              {selectedGroupDetail.isHost ? t('home.closeGroupBtn') : t('home.leaveBtn')}
            </button>
          </div>
        </div>
      )}

      <div className="map-controls">
        {/* 1:1 위치공유 버튼 숨김 처리 (외국인 관광객 타겟 전환)
        <button
          id="btn-1to1-toggle"
          className={is1to1Visible ? styles.toggle1to1BtnActive : styles.toggle1to1Btn}
          onClick={() => setIs1to1Visible(!is1to1Visible)}
          title={t('privateSharing.title')}
          aria-label={t('privateSharing.title')}
        >
          <Shield size={22} />
        </button>
        */}

        <button
          id="btn-my-location"
          className="btn-icon"
          onClick={handleMyLocation}
          aria-label={t('map.myLocation')}
        >
          <Navigation size={18} strokeWidth={2.5} color="var(--color-sky)" />
        </button>
        {(isDesktop || localStorage.getItem('ansim_manual_coords')) && (
          <button
            id="btn-edit-location"
            className="btn-icon"
            onClick={() => setLocationPermissionDenied(true)}
            aria-label="위치 수동 설정"
            title={localStorage.getItem('ansim_manual_coords') ? '수동 위치 재설정' : '내 위치 수동 설정'}
            style={{ marginTop: '8px', border: localStorage.getItem('ansim_manual_coords') ? '2px solid #ef4444' : '2px solid var(--color-sky)' }}
          >
            <MapPin size={18} color={localStorage.getItem('ansim_manual_coords') ? '#ef4444' : 'var(--color-sky)'} />
          </button>
        )}
        <button
          id="btn-zoom-in"
          className="btn-icon"
          onClick={() => setLevel(Math.max(1, getLevel() - 1))}
          aria-label={t('map.zoomIn')}
        >
          <ZoomIn size={18} />
        </button>
        <button
          id="btn-zoom-out"
          className="btn-icon"
          onClick={() => setLevel(Math.min(14, getLevel() + 1))}
          aria-label={t('map.zoomOut')}
        >
          <ZoomOut size={18} />
        </button>
        <button
          id="btn-refresh"
          className="btn-icon"
          onClick={loadCongestionData}
          aria-label={t('map.retry')}
        >
          <RefreshCw size={16} className={loading ? styles.spinning : ''} />
        </button>
      </div>

      {/* 혼잡도 범례 겸 필터 */}
      <div className={`${styles.legend} ${(!isPremium && !IS_FREE_LAUNCH_PHASE) ? styles.hasAd : ''}`}>
        {[
          { level: '전체', color: 'var(--color-text-secondary)', isAll: true },
          { level: '여유', color: 'var(--congestion-1)', lvlNum: 1 },
          { level: '보통', color: 'var(--congestion-2)', lvlNum: 2 },
          { level: '약간 붐빔', color: 'var(--congestion-3)', lvlNum: 3 },
          { level: '붐빔', color: 'var(--congestion-4)', lvlNum: 4 },
          { level: '매우 붐빔', color: 'var(--congestion-5)', lvlNum: 5 },
        ].map(({ level, color, isAll, lvlNum }) => (
          <div 
            key={level} 
            className={`${styles.legendItem} ${selectedCongestion === (isAll ? 'all' : level) ? styles.legendItemActive : ''}`}
            onClick={() => {
              setSelectedCongestion(isAll ? 'all' : level);
              setSelectedSpot(null);
              setSheetExpanded(false);
              clearRoute();
              setSelectedRouteMember(null);
              setRouteInfo(null);
            }}
            role="button"
            aria-label={`혼잡도 ${level} 필터`}
          >
            {isAll ? (
              <span className={styles.legendDot} style={{ background: color, borderRadius: '2px' }} />
            ) : (
              <span className={styles.legendDot} style={{ background: color }} />
            )}
            <span className={styles.legendLabel}>
              {isAll ? t('category.all') : t(`congestion.level${lvlNum}`)}
            </span>
            {selectedCongestion === (isAll ? 'all' : level) && (
              <Check size={12} className={styles.checkIcon} />
            )}
          </div>
        ))}
      </div>

      {/* 하단 정보 시트 */}
      {selectedSpot && (
        <CongestionBottomSheet
          spot={selectedSpot}
          data={selectedData}
          expanded={sheetExpanded}
          onToggle={() => setSheetExpanded(p => !p)}
          onClose={() => { setSelectedSpot(null); setSheetExpanded(false); }}
          onRouteClick={() => {
            setSheetExpanded(false);
            const target = {
              lat: selectedSpot.lat,
              lng: selectedSpot.lng,
              nickname: (i18n.language === 'en' && selectedSpot.en) ? selectedSpot.en : (selectedSpot.name || '목적지')
            };
            fetchRoute(target, 'pedestrian');
          }}
        />
      )}
    </div>
  );
}
